import { CircularProgress, Typography } from '@mui/material'
import { useContext, useEffect, useMemo, useState } from 'react'
import AppContext from '../../context/appContext'
import { useFormDefinitions } from '../../hooks/data/useFormDefinitions'
import { useMetricEvents } from '../../hooks/data/useMetricEvents'
import { ExpandableDataTable } from '../ExpandableDataTable/ExpandableDataTable'
import {
  createColumns,
  processRecordData,
  sortRowsByPaddocks,
} from '../ExpandableDataTable/dataTableHelpers'

const ReportMetricsTable = ({ farmId, namespace, year, farmSubdivision, onMetricLoaded }) => {
  const [tableData, setTableData] = useState({ rowData: null, columns: null })
  const [hasBuildError, setHasBuildError] = useState(false)
  const { data: metricEvents, isError } = useMetricEvents({ farmId, namespace, year })
  const { programConfig } = useContext(AppContext)

  const { data: formDefinitions, isLoading: isFormDefinitionsLoading } =
    useFormDefinitions(programConfig)

  const formDefinition = useMemo(
    () => formDefinitions?.find(fd => fd.namespace === namespace),
    [formDefinitions, namespace],
  )

  useEffect(() => {
    let isCurrent = true

    async function setUpData() {
      // Settling feeds the attestation PDF gate, so only a successful read may settle the metric.
      if (!formDefinition || !farmSubdivision || !metricEvents || isError) return

      try {
        if (metricEvents.length) {
          const newColumns = await createColumns(
            formDefinition,
            ['metricYear', 'paddocks'],
            [
              { name: 'metricYear', options: { width: 40 } },
              { name: 'paddocks', options: { width: 200 } },
            ],
          )
          const rows = await Promise.all(
            metricEvents.map(async record =>
              processRecordData({
                record,
                formDefinition,
                farmSubdivision,
              }),
            ),
          )
          if (!isCurrent) return
          setTableData({ columns: newColumns, rowData: sortRowsByPaddocks(rows) })
        }
        setHasBuildError(false)
        onMetricLoaded(namespace)
      } catch {
        if (isCurrent) setHasBuildError(true)
      }
    }
    setUpData()

    return () => {
      isCurrent = false
    }
  }, [formDefinition, metricEvents, isError, farmSubdivision, onMetricLoaded, namespace])

  const { rowData, columns } = tableData || {}

  // A failed background refetch keeps the last data; a settled table must not revert to the error.
  if ((isError && !metricEvents) || hasBuildError)
    return <Typography color="error">No se pudieron cargar las métricas de este año.</Typography>

  // Not isLoading: a read paused offline has no data yet while reporting neither loading nor error.
  if (!metricEvents || isFormDefinitionsLoading) return <CircularProgress />

  if (!metricEvents.length)
    return <Typography typography="h6">No hay métricas para este año</Typography>

  if (metricEvents.some(event => event.data?.notApplicable))
    return <Typography typography="h6">{formDefinition.requiredMessage || 'No Aplica'}</Typography>

  return (
    columns &&
    rowData && (
      <ExpandableDataTable
        isAllExpanded
        columns={columns}
        customOptions={{
          muiExpandAllButtonProps: {
            sx: {
              display: 'none',
            },
          },
        }}
        data={rowData}
        excludedDataKeys={['findings', 'isVerified', 'metricStatusId', 'notApplicable']}
        formDefinition={formDefinition}
      />
    )
  )
}

export default ReportMetricsTable
